import { useMemo } from "react"
import { Streamdown, type Components as StreamdownComponents } from "streamdown"
import { code } from "@streamdown/code"
import { AIChatLink } from "./AIChatLink"
import {
  AIChatMessageContext,
  extractImageUrlsFromMarkdown,
} from "./AIChatMessageContext"
import { toAgentMarkdown } from "@/lib/markdownText"
import SlickWidgetRenderer, {
  SLICK_WIDGET_LANGUAGE,
} from "@/components/chat/SlickWidgetRenderer"

const streamdownPlugins = {
  code,
  renderers: [
    { language: SLICK_WIDGET_LANGUAGE, component: SlickWidgetRenderer },
  ],
}
// The agent streams whole blocks (a paragraph, a table row), not tokens. Fade
// each new block in as a whole so it does not pop in. No word stagger: it
// would show a table's empty grid first and fill it cell by cell.
// Streamdown applies it only while `isAnimating`, so a finished answer is still.
const streamingAnimation = {
  animation: "fadeIn",
  duration: 600,
  stagger: 0,
} as const
const streamdownComponents: StreamdownComponents = {
  a: AIChatLink as StreamdownComponents["a"],
}

/**
 * The agent's answer text, as the Slick AI chat and a driver room show it,
 * streaming or finished. The surface around it picks the typography.
 */
export function AIChatAnswer({
  text,
  isStreaming,
  className,
}: {
  text: string
  isStreaming: boolean
  className?: string
}) {
  const imageUrls = useMemo(() => extractImageUrlsFromMarkdown(text), [text])
  const contextValue = useMemo(() => ({ imageUrls }), [imageUrls])

  return (
    <AIChatMessageContext.Provider value={contextValue}>
      <Streamdown
        className={className}
        plugins={streamdownPlugins}
        components={streamdownComponents}
        isAnimating={isStreaming}
        animated={streamingAnimation}
      >
        {toAgentMarkdown(text)}
      </Streamdown>
    </AIChatMessageContext.Provider>
  )
}
